/**
 * Monotone cubic interpolation (Fritsch–Carlson, 1980) plus a cached inverse table.
 *
 * Used for stage–storage (HYDRAULICS §2). Monotonicity is not cosmetic: surface area is the
 * analytic derivative `A(h) = dV/dh`, and a plain natural cubic overshoots between knots, which
 * would give a reservoir a negative surface area. Fritsch–Carlson limits the knot tangents so
 * overshoot is impossible by construction.
 *
 * Reference: F.N. Fritsch and R.E. Carlson, "Monotone Piecewise Cubic Interpolation",
 * SIAM J. Numer. Anal. 17(2), 1980.
 */

import { at, clamp } from './constants';

export interface MonotoneCurve {
  readonly xs: Float64Array;
  readonly ys: Float64Array;
  /** Hermite tangents at the knots. */
  readonly ms: Float64Array;
  /** y(x), clamped-linear outside the knot range. */
  eval(x: number): number;
  /** dy/dx at x. */
  slope(x: number): number;
}

export function makeMonotoneCurve(xsIn: readonly number[], ysIn: readonly number[]): MonotoneCurve {
  const n = xsIn.length;
  if (n < 2) throw new Error('monotone curve needs at least two knots');
  if (ysIn.length !== n) throw new Error('monotone curve x/y length mismatch');

  const xs = Float64Array.from(xsIn);
  const ys = Float64Array.from(ysIn);
  for (let i = 1; i < n; i++) {
    if (!(at(xsIn, i) > at(xsIn, i - 1))) throw new Error('monotone curve x must be increasing');
  }

  // 1. Secants.
  const delta = new Float64Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    delta[i] = (ys[i + 1]! - ys[i]!) / (xs[i + 1]! - xs[i]!);
  }

  // 2. Initial tangents: average of adjacent secants, one-sided at the ends.
  const ms = new Float64Array(n);
  ms[0] = delta[0]!;
  ms[n - 1] = delta[n - 2]!;
  for (let i = 1; i < n - 1; i++) {
    ms[i] = (delta[i - 1]! + delta[i]!) / 2;
  }

  // 3. Fritsch–Carlson limiter: keep (α, β) inside the circle of radius 3.
  for (let i = 0; i < n - 1; i++) {
    const d = delta[i]!;
    if (d === 0) {
      ms[i] = 0;
      ms[i + 1] = 0;
      continue;
    }
    const alpha = ms[i]! / d;
    const beta = ms[i + 1]! / d;
    const s = alpha * alpha + beta * beta;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      ms[i] = tau * alpha * d;
      ms[i + 1] = tau * beta * d;
    }
    // A tangent of the wrong sign would break monotonicity outright.
    if (alpha < 0) ms[i] = 0;
    if (beta < 0) ms[i + 1] = 0;
  }

  function findCell(x: number): number {
    // Binary search for the cell containing x; clamped to [0, n-2].
    let lo = 0;
    let hi = n - 2;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (xs[mid]! <= x) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  return {
    xs,
    ys,
    ms,
    eval(x: number): number {
      if (x <= xs[0]!) return ys[0]! + ms[0]! * (x - xs[0]!);
      if (x >= xs[n - 1]!) return ys[n - 1]! + ms[n - 1]! * (x - xs[n - 1]!);
      const i = findCell(x);
      const h = xs[i + 1]! - xs[i]!;
      const t = (x - xs[i]!) / h;
      const t2 = t * t;
      const t3 = t2 * t;
      // Hermite basis.
      const h00 = 2 * t3 - 3 * t2 + 1;
      const h10 = t3 - 2 * t2 + t;
      const h01 = -2 * t3 + 3 * t2;
      const h11 = t3 - t2;
      return h00 * ys[i]! + h10 * h * ms[i]! + h01 * ys[i + 1]! + h11 * h * ms[i + 1]!;
    },
    slope(x: number): number {
      if (x <= xs[0]!) return ms[0]!;
      if (x >= xs[n - 1]!) return ms[n - 1]!;
      const i = findCell(x);
      const h = xs[i + 1]! - xs[i]!;
      const t = (x - xs[i]!) / h;
      const t2 = t * t;
      const d00 = 6 * t2 - 6 * t;
      const d10 = 3 * t2 - 4 * t + 1;
      const d01 = -6 * t2 + 6 * t;
      const d11 = 3 * t2 - 2 * t;
      return (d00 * ys[i]! + d01 * ys[i + 1]!) / h + d10 * ms[i]! + d11 * ms[i + 1]!;
    },
  };
}

/**
 * Inverse of a strictly increasing curve, by a precomputed uniform table in x.
 *
 * Built once per site at module load and treated as read-only, so every clone shares it
 * (DECISIONS D5). For stage–storage the step is 1 cm over 707–762 m, which round-trips far
 * inside the 1 cm tolerance in Section 9.
 */
export interface InverseTable {
  /** x such that curve.eval(x) ≈ y. */
  invert(y: number): number;
}

export function makeInverseTable(
  curve: MonotoneCurve,
  xMin: number,
  xMax: number,
  step: number,
): InverseTable {
  const count = Math.round((xMax - xMin) / step) + 1;
  const ys = new Float64Array(count);
  for (let i = 0; i < count; i++) ys[i] = curve.eval(xMin + i * step);

  for (let i = 1; i < count; i++) {
    if (!(ys[i]! > ys[i - 1]!)) {
      throw new Error(
        `curve is not strictly increasing at x=${(xMin + i * step).toFixed(4)} — cannot invert`,
      );
    }
  }

  return {
    invert(y: number): number {
      const yLo = ys[0]!;
      const yHi = ys[count - 1]!;
      if (y <= yLo) return xMin;
      if (y >= yHi) return xMax;
      // Binary search the bracketing cell, then interpolate linearly inside it.
      let lo = 0;
      let hi = count - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (ys[mid]! <= y) lo = mid;
        else hi = mid;
      }
      const y0 = ys[lo]!;
      const y1 = ys[hi]!;
      const frac = y1 > y0 ? (y - y0) / (y1 - y0) : 0;
      return xMin + (lo + clamp(frac, 0, 1)) * step;
    },
  };
}
